import { Component, computed, inject, input, OnDestroy, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { CropFieldWithIrrigation, Irrigation } from '@app/irrigations/models/models';
import { IrrigationDeleteEvent } from '@app/irrigations/models/ui-models';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { IrrigationTypePipe } from '@app/irrigations/pipes/irrigation-type.pipe';

@Component({
  selector: 'app-irrigation-dialog-overview',
  templateUrl: './irrigation-dialog-overview.component.html',
  styleUrls: ['./irrigation-dialog-overview.component.scss'],
  imports: [
    DatePipe,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatRow,
    MatRowDef,
    MatTable,
    TranslatePipe,
    IrrigationTypePipe,
  ],
})
export class IrrigationDialogOverviewComponent implements OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly dialogService = inject(DialogService);

  private readonly destroy$ = new Subject<boolean>();

  readonly cropFieldsWithIrrigations = input.required<CropFieldWithIrrigation[]>();
  readonly readOnly = input(false);
  readonly irrigationDeleted = output<IrrigationDeleteEvent>();

  protected readonly displayedColumns = computed(() =>
    this.readOnly() ? ['start', 'end', 'type', 'amount'] : ['start', 'end', 'type', 'amount', 'actions'],
  );

  protected readonly locale = toSignal(
    this.localeService.localeDataChanged$.pipe(
      map(() => this.localeService.getLocale()),
      startWith(this.localeService.getLocale()),
    ),
    { initialValue: this.localeService.getLocale() },
  );

  protected onDeleteIrrigation(cropFieldId: number, irrigation: Irrigation): void {
    if (this.readOnly()) {
      return;
    }

    this.dialogService
      .confirm({
        text: this.localeService.translate('blight.irrigation.deleteConfirmMessage'),
        pushHistoryState: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.irrigationDeleted.emit({ cropFieldId, irrigation });
        }
      });
  }

  protected isZeroDurationIrrigation(irrigation: Irrigation): boolean {
    return irrigation.startedOn.getTime() === irrigation.endedOn.getTime();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
